// React & Libraries
import React from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

// Ant Design
import {
  Tooltip, DatePicker
} from 'antd';
import {
  UserOutlined, LinkOutlined, EditFilled, CheckOutlined
} from '@ant-design/icons';

// UI Libraries
import H3Text from '@Uilib/h3Text';

// Constants & Helpers
import Helpers from '@Apis/helpers';

// Common Components
import ViewCustomFields from '@Components/Common/CustomField/ViewCustomFields';
import SelectPaymentTerm from '@Components/Common/SelectPaymentTerm';
import SelectAppUser from '@Components/Common/SelectAppUser';

// Local components
import { getShopifyStatusProps } from '../helper';

function ViewSOLeftSectionDetails({
  props, state, updateState, quickUpdateDisable, hasSoEditAccess, accountingGSTTransactionAsPerMaster
}) {

  const {
    user, getSOById, isQuickView, selectedSO, quickUpdateSO, getActivityLog
  } = props;

  const {
    updatedGstNumber, isGstNumberEditable, currentTenantId, currentOrderId, updateAccountManager, updatedPaymentTerms, updateDeliveryDate, updateEstimateExpiryDate, isPaymentRemark, updatePaymentRemark, salesOrderType, isEstimate
  } = state;

  return (
    <div className="document-header">
      <div className="ant-row">
        <div className="ant-col-md-24">
          <H3Text
            text={(
              <span>
                <UserOutlined />
                {' '}
                <span>{`${selectedSO?.internal_cust_code} - ${selectedSO?.customer_info?.customer_name}`}</span>
              </span>
            )}
            className="document-header__party-name"
          />
        </div>
        <div className="ant-col-md-12">
          <Tooltip title={selectedSO?.tenant_info?.tenant_name}>
            <div className="document-header__field">
              <H3Text text="Location" className="document-header__field-name" />
              <H3Text text={selectedSO?.tenant_info?.tenant_name?.length > 25 ? `${selectedSO?.tenant_info?.tenant_name?.substring(0, 25)}...` : selectedSO?.tenant_info?.tenant_name} className="document-header__field-value" />
            </div>
          </Tooltip>
        </div>
        <div className="ant-col-md-12">
          <div className="document-header__field">
            <H3Text text="Department" className="document-header__field-name" />
            <H3Text text={selectedSO?.tenant_department_info?.department_name} className="document-header__field-value" />
          </div>
        </div>
        <div className="ant-col-md-12">
          <div className="document-header__field">
            <H3Text text={isEstimate ? "Estimate Date" : "Order Date"} className="document-header__field-name" />
            <H3Text
              text={selectedSO?.order_date ? (dayjs(selectedSO.order_date).format('DD/MM/YYYY')) : '-'}
              className="document-header__field-value"
            />
          </div>
        </div>
        <div className="ant-col-md-12">
          <div className="document-header__field">
            <H3Text text="Delivery Date" className="document-header__field-name" />
            {quickUpdateDisable ? (
              <div className="document-header__field-input">
                <DatePicker
                  value={updateDeliveryDate}
                  onChange={(value) => {
                    updateState({ updateDeliveryDate: value });
                    quickUpdateSO({
                      delivery_date: dayjs(value).format('YYYY/MM/DD'),
                      order_id: selectedSO.order_id,
                      orderType: salesOrderType,
                    }, () => {
                      getActivityLog(selectedSO.order_id, salesOrderType?.toLowerCase(), 1, 10);
                      updateState({ isUserReadyForPrefillData: true });
                    }, () => {
                      updateState({ isUserReadyForPrefillData: false });
                    });
                  }}
                  format="MMM DD, YYYY"
                  allowClear={false}
                  disabled={!hasSoEditAccess}
                />
              </div>
            ) : (
              <H3Text
                text={selectedSO?.delivery_date ? (dayjs(selectedSO.delivery_date).format('DD/MM/YYYY')) : '-'}
                className="document-header__field-value"
              />
            )}

          </div>
        </div>
        {selectedSO?.shopify_order_id ? (
          !!selectedSO?.shopify_payment_modes?.length && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text text="Payment Mode" className="document-header__field-name" />
                <H3Text
                  text={selectedSO?.shopify_payment_modes?.[0]?.replaceAll('_', ' ')?.toProperCase()}
                  className="document-header__field-value"
                />
              </div>
            </div>
          )
        ) : (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Payment Terms" className="document-header__field-name" />
              <SelectPaymentTerm
                selectedPaymentTerm={updatedPaymentTerms}
                onChange={(value) => {
                  updateState({ updatedPaymentTerms: value?.due_days });
                  const paymentTerms = [
                    {
                      due_days: value?.due_days,
                    },
                  ];
                  quickUpdateSO({
                    payment_terms: paymentTerms || [],
                    order_id: selectedSO.order_id,
                    orderType: salesOrderType,
                  }, () => {
                    getActivityLog(selectedSO.order_id, salesOrderType?.toLowerCase(), 1, 10);
                    updateState({ isUserReadyForPrefillData: true });
                  }, () => {
                    updateState({ isUserReadyForPrefillData: false });
                  });
                }}
                callback={(value) => {
                  updateState({ updatedPaymentTerms: Number(value) });
                  const paymentTerms = [
                    {
                      due_days: value,
                    },
                  ];
                  quickUpdateSO({
                    payment_terms: paymentTerms || [],
                    order_id: selectedSO.order_id,
                    orderType: salesOrderType,
                  }, () => {
                    getActivityLog(selectedSO.order_id, salesOrderType?.toLowerCase(), 1, 10);
                    updateState({ isUserReadyForPrefillData: true });
                  }, () => {
                    updateState({ isUserReadyForPrefillData: false });
                  });
                }}
                placeholder="Select Payment Term"
                hideTitle
                inputClassName="document-header__field-input"
                disabled={!quickUpdateDisable || !hasSoEditAccess}
                showAddPaymentTerm
              />
            </div>
          </div>
        )}
        <div className="ant-col-md-12">
          <div className="document-header__field">
            <H3Text text="Payment Remarks" className="document-header__field-name" />
            {(isPaymentRemark) && (
              <div className="document-header__field-input">
                <input
                  value={updatePaymentRemark}
                  onChange={(e) => {
                    updateState({ updatePaymentRemark: e.target.value });
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      updateState({ isPaymentRemark: false });
                      const paymentTerms = [
                        {
                          remark: updatePaymentRemark,
                        },
                      ];
                      quickUpdateSO({
                        payment_terms: paymentTerms || [],
                        order_id: selectedSO.order_id,
                        orderType: salesOrderType,
                      }, () => {
                        getActivityLog(selectedSO.order_id, salesOrderType?.toLowerCase(), 1, 10);
                        updateState({ isUserReadyForPrefillData: true });
                      }, () => {
                        updateState({ isUserReadyForPrefillData: false });
                      });
                    }
                  }}
                  className="document-custom-input"
                  disabled={!hasSoEditAccess}
                />
                &nbsp;
                <CheckOutlined
                  style={{ color: '#1890ff', cursor: 'pointer' }}
                  onClick={() => {
                    updateState({ isPaymentRemark: false });
                    const paymentTerms = [
                      {
                        remark: updatePaymentRemark,
                      },
                    ];
                    quickUpdateSO({
                      payment_terms: paymentTerms || [],
                      order_id: selectedSO.order_id,
                      orderType: salesOrderType,
                    }, () => {
                      getActivityLog(selectedSO.order_id, salesOrderType?.toLowerCase(), 1, 10);
                      updateState({ isUserReadyForPrefillData: true });
                    }, () => {
                      updateState({ isUserReadyForPrefillData: false });
                    });
                  }}
                />
              </div>
            )}
            {(!isPaymentRemark) && (
              <div
                className="document-header__field-value"
              >
                {updatePaymentRemark && (
                  <H3Text
                    text={updatePaymentRemark}
                  />
                )}
                {quickUpdateDisable && hasSoEditAccess && (
                  <EditFilled
                    className="edit-icon"
                    onClick={() => {
                      updateState({ isPaymentRemark: true });
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        {selectedSO?.conversion_rate && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Conversion Rate" className="document-header__field-name" />
              <H3Text text={`${selectedSO?.conversion_rate}`} className="document-header__field-value" />
            </div>
          </div>
        )}
        {isEstimate && (<div className="ant-col-md-12">
          <div className="document-header__field">
            <H3Text text="Estimate Expiry Date" className="document-header__field-name" />
            {quickUpdateDisable ? (
              <div className="document-header__field-input">
                <DatePicker
                  value={updateEstimateExpiryDate}
                  onChange={(value) => {
                    updateState({ updateEstimateExpiryDate: value });
                    quickUpdateSO({
                      estimate_expiry_date: dayjs(value).format('YYYY/MM/DD'),
                      order_id: selectedSO.order_id,
                      orderType: salesOrderType,
                    }, () => {
                      getActivityLog(selectedSO.order_id, salesOrderType?.toLowerCase(), 1, 10);
                      updateState({ isUserReadyForPrefillData: true });
                    }, () => {
                      updateState({ isUserReadyForPrefillData: false });
                    });
                  }}
                  format="MMM DD, YYYY"
                  allowClear={false}
                  disabled={!hasSoEditAccess}
                />
              </div>
            ) : (
              <H3Text
                text={selectedSO?.estimate_expiry_date ? (dayjs(selectedSO.estimate_expiry_date).format('DD/MM/YYYY')) : '-'}
                className="document-header__field-value"
              />
            )}

          </div>
        </div>
        )}
        {selectedSO?.customer_info?.email_address && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Email" className="document-header__field-name" />
              <Tooltip overlayClassName="custom-tooltip"
                color="rgb(234, 242, 254)"
                title={(
                  <div
                    style={{ color: 'black', fontSize: '13px' }}
                    className="tooltip-content"
                  >
                    {selectedSO?.customer_info?.email_address || '-'}
                  </div>
                )}
              >
                <span>
                  <H3Text
                    text={selectedSO?.customer_info?.email_address || '-'}
                    className="document-header__field-value ellipses"
                  />
                </span>
              </Tooltip>

            </div>
          </div>
        )}
        {selectedSO?.mrp_id && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="MRP Number" className="document-header__field-name" />
              <Link
                style={{ color: 'rgb(45, 125, 247)' }}
                to={`/production/planning/v2/view/${selectedSO?.mrp_id}`}
                target="_blank"
              >
                {selectedSO?.mrp_number || '-'}
              </Link>
            </div>
          </div>
        )}
        {selectedSO?.customer_info?.phone_number && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Phone" className="document-header__field-name" />
              <H3Text
                text={selectedSO?.customer_info?.phone_number || '-'}
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        <div className="ant-col-md-12">
          <div className="document-header__field">
            <H3Text text="GSTIN" className="document-header__field-name" />
            {(isGstNumberEditable) && (
              <div className="document-header__field-input ">
                <input
                  value={updatedGstNumber}
                  onChange={(e) => {
                    updateState({ updatedGstNumber: e.target.value });
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      updateState({ isGstNumberEditable: false });
                      quickUpdateSO({
                        gst_number: updatedGstNumber || null,
                        order_id: selectedSO.order_id,
                        orderType: salesOrderType,
                      }, () => {
                        getActivityLog(selectedSO.order_id, salesOrderType?.toLowerCase(), 1, 10);
                        updateState({ isUserReadyForPrefillData: true });
                      }, () => {
                        updateState({ isUserReadyForPrefillData: false });
                      });
                    }
                  }}
                  className="document-custom-input"
                  maxLength="15"
                  disabled={!hasSoEditAccess}
                />
                &nbsp;
                <CheckOutlined
                  style={{ color: '#1890ff', cursor: 'pointer' }}
                  onClick={() => {
                    updateState({ isGstNumberEditable: false });
                    quickUpdateSO({
                      gst_number: updatedGstNumber || null,
                      order_id: selectedSO.order_id,
                      orderType: salesOrderType,
                    }, () => {
                      getActivityLog(selectedSO.order_id, salesOrderType?.toLowerCase(), 1, 10);
                      updateState({ isUserReadyForPrefillData: true });
                    }, () => {
                      updateState({ isUserReadyForPrefillData: false });
                    });
                  }}
                />
              </div>
            )}
            {(!isGstNumberEditable) && (
              <div className="document-header__field-value">
                {updatedGstNumber && (
                  <H3Text
                    text={updatedGstNumber}
                  />
                )}
                {(quickUpdateDisable && hasSoEditAccess && !accountingGSTTransactionAsPerMaster) && (
                  <EditFilled
                    className="edit-icon"
                    onClick={() => {
                      updateState({ isGstNumberEditable: true });
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        {selectedSO?.price_list_details && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Price List" className="document-header__field-name" />
              <H3Text
                text={selectedSO?.price_list_details?.price_list_name || '-'}
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        <div className="ant-col-md-12">
          <div className="document-header__field update__account-manager">
            <H3Text text="Sales Manager" className="document-header__field-name" />
            {quickUpdateDisable ? (
              <SelectAppUser
                hideTitle
                selectedUser={updateAccountManager}
                onChange={(value) => {
                  updateState({ updateAccountManager: value });
                  quickUpdateSO({
                    account_manager_id: value || null,
                    order_id: selectedSO.order_id,
                    orderType: salesOrderType,
                  }, () => {
                    getActivityLog(selectedSO.order_id, salesOrderType?.toLowerCase(), 1, 10);
                    updateState({ isUserReadyForPrefillData: true });
                  }, () => {
                    updateState({ isUserReadyForPrefillData: false });
                  });
                }}
                containerClassName="document-header__field-input"
                disabled={!hasSoEditAccess}
              />
            ) : (
              <H3Text
                text={`${selectedSO?.account_manager_info?.first_name || ''} ${selectedSO?.account_manager_info?.last_name || ''}`}
                className="document-header__field-value"
              />
            )}
          </div>
        </div>
        {selectedSO?.zoho_order_id && !isQuickView && (
          <div className="ant-col-md-12">
            <Tooltip title={selectedSO?.zoho_order_number}>
              <div className="document-header__field">
                <H3Text text="Zoho" className="document-header__field-name" />
                <H3Text
                  text={selectedSO?.zoho_order_number || '-'}
                  className="document-header__field-value"
                />
              </div>
            </Tooltip>
          </div>
        )}
        {selectedSO?.unicommerce_so_code && !isQuickView && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text text="Unicommerce" className="document-header__field-name" />
              <H3Text
                text={(
                  <a href={`https://ptinventindia.unicommerce.com/order/orderitems?orderCode=${selectedSO?.unicommerce_so_code}`} target="_blank" rel="noreferrer">
                    <span className="status-tag">Pushed</span>
                    <LinkOutlined />
                  </a>
                )}
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        {selectedSO?.shopify_order_id && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text
                text="Shopify Order#"
                className="document-header__field-name"
              />
              <div className="document-header__field-value">
                <a
                  style={{
                    color: 'rgb(45, 125, 247)',
                  }}
                  href={`https://admin.shopify.com/store/${selectedSO?.shopify_shop_name?.split('.')?.[0]}/orders/${selectedSO?.shopify_order_id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {selectedSO?.shopify_order_number}
                </a>

              </div>
            </div>
          </div>
        )}
        {
          selectedSO?.shopify_so_object?.cancelled_at && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text
                  text="Shopify Status"
                  className="document-header__field-name"
                />
                <div className="document-header__field-value">
                  <div className="status-tag" style={{ backgroundColor: 'red' }}>
                    &nbsp;
                    Cancelled
                  </div>
                </div>
              </div>
            </div>
          )
        }
        {selectedSO?.shopify_order_id && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text
                text="Shopify Source"
                className="document-header__field-name"
              />
              <div className="document-header__field-value">
                {selectedSO?.order_client_source?.source}
              </div>
            </div>
          </div>
        )}
        {
          selectedSO?.shopify_order_id && (
            <div className="ant-col-md-12">
              <div className="document-header__field">
                <H3Text
                  text="Delivery"
                  className="document-header__field-name"
                />
                <div className="document-header__field-value">
                  <div className="status-tag" style={{ backgroundColor: getShopifyStatusProps(selectedSO?.shopify_fulfillment_status || 'pending')?.bgColor }}>
                    &nbsp;
                    {selectedSO?.shopify_fulfillment_status?.toProperCase() || 'Pending'}
                  </div>
                </div>
              </div>
            </div>
          )
        }
        {selectedSO?.custom_fields?.length > 0 ? (
          <ViewCustomFields
            customFields={selectedSO?.custom_fields?.filter((cf) => cf.is_active)}
            wrapperClass="document-header__field"
            labelClass="document-header__field-name"
            valueClass="document-header__field-value"
            inputClassName="document-header__field-input"
            isHorizontal
            isEditable={quickUpdateDisable && hasSoEditAccess}
            handleCustomFieldUpdate={quickUpdateSO}
            entityId={selectedSO.order_id}
            callback={() => {
              const payload = {
                orgId: user?.tenant_info?.org_id,
                tenantId: currentTenantId,
                soId: currentOrderId,
                orderType: salesOrderType,
              };
              getSOById(payload);
            }}
            entityName="order_id"
            entityType={window.location.href.includes('/estimate') ? 'SALES_ESTIMATE' : selectedSO?.order_type === 'ANNUAL_MAINTENANCE_CONTRACT' ? 'ANNUAL_MAINTENANCE_CONTRACT' : 'SALES_ORDER'}
          />
        ) : ''}
      </div>
    </div>
  );
};

export default ViewSOLeftSectionDetails;